import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Mail, Phone, MapPin, MessageCircle, Send, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { CONTACT } from "../lib/constants";
import { MaskedLines, Reveal } from "./motion";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    products: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const prefill = searchParams.get("prefill");
    if (prefill) {
      setForm((f) => ({
        ...f,
        products: prefill,
        message: f.message || `Me interesa cotizar: ${prefill}.`,
      }));
      // Smoothly focus contact section
      setTimeout(() => {
        const el = document.getElementById("contacto");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 200);
    }
  }, [searchParams]);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Por favor complete los campos obligatorios.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/api/quotations", form);
      toast.success("¡Cotización enviada!", {
        description:
          "Hemos recibido su solicitud. Le contactaremos a la brevedad.",
      });
      setForm({
        name: "",
        company: "",
        email: "",
        phone: "",
        products: "",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "No fue posible enviar la solicitud. Intente nuevamente.";
      toast.error("Error al enviar", { description: String(detail) });
    } finally {
      setLoading(false);
    }
  };

  const whatsappMsg = encodeURIComponent(
    `Hola Pino Verde, me gustaría solicitar una cotización.${
      form.name ? `\nNombre: ${form.name}` : ""
    }${form.company ? `\nEmpresa: ${form.company}` : ""}${
      form.products ? `\nProductos: ${form.products}` : ""
    }`
  );
  const waUrl = `https://wa.me/${CONTACT.whatsappRaw}?text=${whatsappMsg}`;

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="py-24 lg:py-32 bg-pv-green text-white relative overflow-hidden"
      style={{ backgroundColor: "hsl(var(--pv-dark-green))" }}
    >
      <div className="absolute inset-0 bg-blueprint-dark opacity-30" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <Reveal className="lg:col-span-5">
          <div className="overline text-pv-lime mb-4">Capítulo 04 — Contacto</div>
          <MaskedLines
            className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
            lines={[
              <>Solicite su</>,
              <span className="text-pv-orange">cotización hoy.</span>,
            ]}
          />
          <p className="mt-6 text-white/80 text-base lg:text-lg leading-relaxed">
            Cuéntenos qué necesita su empresa. Le responderemos con una
            propuesta a su medida en menos de 24 horas hábiles.
          </p>

          <div className="mt-10 space-y-5">
            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="contact-email"
              className="flex items-start gap-4 group"
            >
              <div className="w-11 h-11 bg-white/10 border border-white/20 rounded-sm flex items-center justify-center group-hover:bg-pv-orange group-hover:border-pv-orange transition-colors shrink-0">
                <Mail size={18} />
              </div>
              <div>
                <div className="overline text-white/60">Correo</div>
                <div className="font-medium">{CONTACT.email}</div>
              </div>
            </a>

            <a
              href={`tel:+506${CONTACT.phone.replace("-", "")}`}
              data-testid="contact-phone"
              className="flex items-start gap-4 group"
            >
              <div className="w-11 h-11 bg-white/10 border border-white/20 rounded-sm flex items-center justify-center group-hover:bg-pv-orange group-hover:border-pv-orange transition-colors shrink-0">
                <Phone size={18} />
              </div>
              <div>
                <div className="overline text-white/60">Teléfono</div>
                <div className="font-medium">
                  (506) {CONTACT.phone} · (506) {CONTACT.phoneAlt}
                </div>
                <div className="text-sm text-white/60">
                  Ventas: (506) {CONTACT.sales}
                </div>
              </div>
            </a>

            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp"
              className="flex items-start gap-4 group"
            >
              <div className="w-11 h-11 bg-white/10 border border-white/20 rounded-sm flex items-center justify-center group-hover:bg-pv-lime group-hover:border-pv-lime transition-colors shrink-0">
                <MessageCircle size={18} />
              </div>
              <div>
                <div className="overline text-white/60">WhatsApp</div>
                <div className="font-medium">(506) {CONTACT.whatsapp}</div>
                <div className="text-sm text-white/60">
                  Respuesta inmediata
                </div>
              </div>
            </a>

            <a
              href={CONTACT.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-address"
              className="flex items-start gap-4 group"
            >
              <div className="w-11 h-11 bg-white/10 border border-white/20 rounded-sm flex items-center justify-center group-hover:bg-pv-orange group-hover:border-pv-orange transition-colors shrink-0">
                <MapPin size={18} />
              </div>
              <div>
                <div className="overline text-white/60">Dirección</div>
                <div className="font-medium">{CONTACT.address}</div>
                <div className="text-sm text-white/60">{CONTACT.city}</div>
              </div>
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            data-testid="quote-form"
            className="bg-white text-slate-900 rounded-sm p-6 sm:p-10 border border-white/10"
          >
            <h3 className="font-display font-bold text-2xl text-pv-green mb-6">
              Formulario de cotización
            </h3>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="sm:col-span-1">
                <Label htmlFor="name" className="text-slate-700">
                  Nombre completo *
                </Label>
                <Input
                  id="name"
                  name="name"
                  data-testid="form-name"
                  required
                  value={form.name}
                  onChange={onChange}
                  placeholder="Juan Pérez"
                  className="mt-1.5 rounded-sm h-11 border-slate-300 focus-visible:ring-pv-green"
                />
              </div>
              <div className="sm:col-span-1">
                <Label htmlFor="company" className="text-slate-700">
                  Empresa
                </Label>
                <Input
                  id="company"
                  name="company"
                  data-testid="form-company"
                  value={form.company}
                  onChange={onChange}
                  placeholder="Nombre de la empresa"
                  className="mt-1.5 rounded-sm h-11 border-slate-300 focus-visible:ring-pv-green"
                />
              </div>
              <div className="sm:col-span-1">
                <Label htmlFor="email" className="text-slate-700">
                  Correo *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  data-testid="form-email"
                  required
                  value={form.email}
                  onChange={onChange}
                  placeholder="correo@empresa.com"
                  className="mt-1.5 rounded-sm h-11 border-slate-300 focus-visible:ring-pv-green"
                />
              </div>
              <div className="sm:col-span-1">
                <Label htmlFor="phone" className="text-slate-700">
                  Teléfono
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  data-testid="form-phone"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="8888-8888"
                  className="mt-1.5 rounded-sm h-11 border-slate-300 focus-visible:ring-pv-green"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="products" className="text-slate-700">
                  Productos de interés
                </Label>
                <Input
                  id="products"
                  name="products"
                  data-testid="form-products"
                  value={form.products}
                  onChange={onChange}
                  placeholder="Ej: Cascos, guantes anticorte, calzado de seguridad"
                  className="mt-1.5 rounded-sm h-11 border-slate-300 focus-visible:ring-pv-green"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="message" className="text-slate-700">
                  Mensaje *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  data-testid="form-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={onChange}
                  placeholder="Cuéntenos sobre su empresa, cantidad de colaboradores y necesidades específicas."
                  className="mt-1.5 rounded-sm border-slate-300 focus-visible:ring-pv-green"
                />
              </div>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button
                type="submit"
                data-testid="form-submit"
                disabled={loading}
                className="rounded-sm bg-pv-orange hover:bg-pv-orange/90 text-white h-12 px-7 font-semibold flex-1 sm:flex-none"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send size={16} className="mr-2" />
                    Enviar Cotización
                  </>
                )}
              </Button>
              <Button
                asChild
                type="button"
                variant="outline"
                data-testid="form-whatsapp"
                className="rounded-sm border-pv-green text-pv-green hover:bg-pv-green hover:text-white h-12 px-7 font-semibold"
              >
                <a href={waUrl} target="_blank" rel="noopener noreferrer">
                  <MessageCircle size={16} className="mr-2" />
                  Enviar por WhatsApp
                </a>
              </Button>
            </div>
            <p className="mt-4 text-xs text-slate-500">
              * Campos obligatorios. Sus datos no se compartirán con terceros.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
